import type { NavigateFunction } from 'react-router-dom'
import { queryClient } from '@/lib/react-query'
import { useAuthStore } from '@/store/useAuthStore'
import { posthog } from '@/lib/posthog'
import { getNextjsApiBaseUrl } from '@/config/apiBaseUrl'

export interface InvitationRedirectInfo {
  role: string
  team_id?: string | null
  is_invite?: boolean
  onboarding_completed?: boolean
}

export interface AuthRedirectPayload {
  path: string
  state?: Record<string, unknown>
}

export interface PostAuthSideEffectsOptions {
  /** false après hydrate session (évite refetch inutiles). Défaut: true */
  invalidateQueries?: boolean
}

/** Redirect depuis le shell session déjà hydraté (impersonation, etc.). */
export function resolveSessionNavigationRedirect(): AuthRedirectPayload {
  const { sessionRequiredPath, sessionDefaultPath } = useAuthStore.getState()

  if (sessionRequiredPath) {
    return { path: sessionRequiredPath, state: { isEdit: false } }
  }

  return { path: sessionDefaultPath || '/missions' }
}

export function navigateToAuthRedirect(
  navigate: NavigateFunction,
  redirect: AuthRedirectPayload
): void {
  navigate(redirect.path, {
    replace: true,
    ...(redirect.state ? { state: redirect.state } : {}),
  })
}

export async function resolvePostAuthRedirect(params: {
  redirect: AuthRedirectPayload | null
  redirectUrl?: string | null
  invitation?: InvitationRedirectInfo | null
}): Promise<AuthRedirectPayload> {
  if (params.redirect?.path) {
    return params.redirect
  }

  if (params.redirectUrl || params.invitation) {
    return fetchPostAuthRedirect({
      redirectUrl: params.redirectUrl,
      invitation: params.invitation,
    })
  }

  return resolveSessionNavigationRedirect()
}

export function runPostAuthSideEffects(
  role: string | null,
  options?: PostAuthSideEffectsOptions
): void {
  const { initActiveTeam, user, impersonating, activeTeamId } = useAuthStore.getState()

  // Ne pas appeler switchTeam pendant l'impersonation (session shell déjà complète)
  // Session POST /login inclut déjà active_team_id — skip si présent
  if (role && role !== 'super-admin' && initActiveTeam && !impersonating && !activeTeamId) {
    void initActiveTeam().catch((error) => {
      console.error('[finalizeAuthenticatedSession] Error initializing active team:', error)
    })
  }

  if (options?.invalidateQueries !== false) {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.invalidateQueries({ queryKey: ['companyOnboardingStatus'] })
      queryClient.invalidateQueries({ queryKey: ['freelancerOnboardingStatus'] })
      queryClient.invalidateQueries({ queryKey: ['userTeams'] })
      queryClient.invalidateQueries({ queryKey: ['teamRole'] })
    }, 0)
  }

  const currentUser = user ?? useAuthStore.getState().user
  if (currentUser?.id) {
    try {
      posthog.identify(currentUser.id, {
        email: currentUser.email ?? undefined,
        first_name: currentUser.first_name ?? undefined,
        last_name: currentUser.last_name ?? undefined,
      })
    } catch {
      // PostHog non bloquant
    }
  }
}

export function applyAuthRole(role: string | null): void {
  if (!role) return

  const { globalRole, user } = useAuthStore.getState()
  if (role === globalRole) return

  useAuthStore.setState({ globalRole: role })
  if (user) {
    useAuthStore.setState({
      user: { ...user, role },
    })
  }
}

export async function fetchPostAuthRedirect(params: {
  redirectUrl?: string | null
  invitation?: InvitationRedirectInfo | null
}): Promise<AuthRedirectPayload> {
  const nextjsApiUrl = getNextjsApiBaseUrl()
  const response = await fetch(`${nextjsApiUrl}/api/app/v1/auth/post-auth-redirect`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      redirect_url: params.redirectUrl ?? null,
      invitation: params.invitation ?? null,
    }),
  })

  const result = await response.json()
  if (!response.ok || result.code !== 200 || !result.data?.redirect?.path) {
    throw new Error(result.message || 'Failed to resolve post-auth redirect')
  }

  return result.data.redirect as AuthRedirectPayload
}
